import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import {
  createParameter,
  getDeviceParameters,
  updateParameter,
  deleteParameter,
} from "../api/admin";
import { ParameterCreateDto, ParameterUpdateDto } from "../types/device";
import { ParameterFormData } from "../components/widgets/table/AddParameterModal";

/**
 * Hook to fetch device parameters
 * @param deviceId - The device ID to fetch parameters for
 * @param enabled - Whether the query should be enabled
 * @returns Query result with device parameters
 */
export function useGetDeviceParameters(
  deviceId?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["parameters", "device", deviceId],
    queryFn: getDeviceParameters,
    enabled: !!deviceId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch parameters for multiple devices in parallel and return a
 * deduplicated list keyed by parameter `identifier`. The first occurrence of
 * each identifier wins, preserving that parameter's metadata (name/unit/range).
 *
 * The merged `data` reference is stabilized via a signature cache so downstream
 * effects don't fire on every render.
 */
export function useGetMultiDeviceParameters(deviceIds: string[]) {
  const results = useQueries({
    queries: deviceIds.map((id) => ({
      queryKey: ["parameters", "device", id] as const,
      queryFn: getDeviceParameters,
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    })),
  });

  const isLoading = results.length > 0 && results.some((r) => r.isLoading);
  const error = results.find((r) => r.error)?.error ?? null;

  // Build a signature that changes only when any underlying query data changes.
  const signature =
    deviceIds
      .map((id, i) => `${id}:${results[i]?.dataUpdatedAt ?? 0}`)
      .join("|");

  const cacheRef = useRef<{ sig: string; data: ParameterFormData[] }>({
    sig: "",
    data: [],
  });

  if (cacheRef.current.sig !== signature) {
    const seen = new Set<string>();
    const merged: ParameterFormData[] = [];
    for (const r of results) {
      if (!r.data) continue;
      for (const p of r.data) {
        if (!seen.has(p.identifier)) {
          seen.add(p.identifier);
          merged.push(p);
        }
      }
    }
    cacheRef.current = { sig: signature, data: merged };
  }

  return { data: cacheRef.current.data, isLoading, error };
}

/**
 * Hook to create a new parameter
 * @returns Mutation for creating parameters
 */
export function useCreateParameter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["create-parameter"],
    mutationFn: createParameter,
    onSuccess: (_, variables) => {
      // Invalidate and refetch device parameters
      queryClient.invalidateQueries({
        queryKey: ["parameters", "device", variables.deviceId],
      });
    },
    onError: (error) => {
      console.error("Error creating parameter:", error);
    },
  });
}

/**
 * Hook to update an existing parameter
 * @returns Mutation for updating parameters
 */
export function useUpdateParameter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["update-parameter"],
    mutationFn: ({ id, data }: { id: string; data: ParameterUpdateDto }) =>
      updateParameter(id, data),
    onSuccess: () => {
      // Invalidate all device parameters queries
      queryClient.invalidateQueries({ queryKey: ["parameters"] });
    },
    onError: (error) => {
      console.error("Error updating parameter:", error);
    },
  });
}

/**
 * Hook to delete a parameter
 * @returns Mutation for deleting parameters
 */
export function useDeleteParameter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["delete-parameter"],
    mutationFn: deleteParameter,
    onSuccess: () => {
      // Invalidate all device parameters queries
      queryClient.invalidateQueries({ queryKey: ["parameters"] });
    },
    onError: (error) => {
      console.error("Error deleting parameter:", error);
    },
  });
}
